import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api, orgID, post, remove, useData, useSession } from '@yingbaba/api';
import type {
  OrganizationVerificationDocument,
  OrganizationVerificationView,
} from '@yingbaba/types';
import {
  Select,
  Button,
  ErrorNotice,
  Field,
  Notice,
  PageTitle,
  PrivateDocumentPreview,
  QueryState,
  Status,
} from '@yingbaba/ui';
import { ShieldCheck, Upload, Trash2, FileText } from '@yingbaba/icons';
import { datetime } from '@yingbaba/utils';
import { useConfirmLeave, useUnsavedChanges } from './UnsavedChanges';

const subjectLabel: Record<string, string> = {
  individual: '个人主体',
  enterprise: '企业主体',
};
const documentLabel: Record<string, string> = {
  id_front: '身份证人像面',
  id_back: '身份证国徽面',
  business_license: '营业执照',
  legal_representative_id_front: '法定代表人身份证人像面',
  legal_representative_id_back: '法定代表人身份证国徽面',
};
const documentsFor = (subject: string) =>
  subject === 'enterprise'
    ? ['business_license', 'legal_representative_id_front', 'legal_representative_id_back']
    : ['id_front', 'id_back'];

export function OrganizationVerification() {
  const session = useSession();
  const verification = useData<OrganizationVerificationView>('/organization-verification');
  const qc = useQueryClient();
  const organization = session.data?.organizations.find((item) => item.id === orgID());
  const active =
    (!organization?.lifecycle_status || organization.lifecycle_status === 'active') &&
    session.data?.account.lifecycle_status !== 'closing';
  const canVerify = ['owner', 'admin'].includes(organization?.role || '') && active;
  const application = verification.data?.application;
  const status = verification.data?.status || organization?.verification_status || 'unverified';
  const [subject, setSubject] = useState('individual');
  const [legalName, setLegalName] = useState('');
  const [identityNumber, setIdentityNumber] = useState('');
  const [creditCode, setCreditCode] = useState('');
  const [legalRepresentativeName, setLegalRepresentativeName] = useState('');
  const [legalRepresentativeID, setLegalRepresentativeID] = useState('');
  const [reference, setReference] = useState('');
  const [operation, setOperation] = useState('');
  const [scopeOpen, setScopeOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1000px)').matches,
  );
  const busy = !!operation;
  const uploadingKind = operation.startsWith('upload:') ? operation.slice(7) : '';
  const [error, setError] = useState<unknown>(null);
  const documentInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const pending = useRef(false);
  const alive = useRef(true);
  const [baseline, setBaseline] = useState('');
  const [version, setVersion] = useState('new');
  const leave = useConfirmLeave();
  const snapshot = JSON.stringify([
    subject,
    legalName,
    identityNumber,
    creditCode,
    legalRepresentativeName,
    legalRepresentativeID,
    reference,
  ]);
  const dirty = !!baseline && snapshot !== baseline;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ block: 'center' });
      errorRef.current?.focus({ preventScroll: true });
    }
  }, [error]);
  function applyView(view: OrganizationVerificationView) {
    const record = view.application;
    setVersion(record?.status === 'draft' ? record.updated_at : 'new');
    const nextSubject = record?.subject_type || view.subject_type || 'individual';
    const nextName =
      record?.legal_name || (nextSubject === 'enterprise' ? organization?.name || '' : '');
    const nextRepresentative = record?.legal_representative_name || '';
    const nextReference = record?.reference || '';
    setSubject(nextSubject);
    setLegalName(nextName);
    setLegalRepresentativeName(nextRepresentative);
    setReference(nextReference);
    setIdentityNumber('');
    setCreditCode('');
    setLegalRepresentativeID('');
    setBaseline(
      JSON.stringify([nextSubject, nextName, '', '', nextRepresentative, '', nextReference]),
    );
  }
  useEffect(() => {
    if (verification.data && !pending.current && !dirtyRef.current) applyView(verification.data);
  }, [verification.data, organization?.name]);

  const draft = application?.status === 'draft';
  const editable = canVerify && status !== 'verified' && status !== 'pending_review';
  const documents = draft ? verification.data?.documents || [] : [];
  const byKind = useMemo(
    () => Object.fromEntries(documents.map((document) => [document.kind, document])),
    [documents],
  );
  const requiredDocuments = documentsFor(subject);

  const sameSubject = application?.subject_type === subject;
  const missing = [
    ...(legalName.trim().length < 2
      ? [subject === 'enterprise' ? '企业法定名称' : '真实姓名']
      : []),
    ...(subject === 'individual' &&
    !identityNumber.trim() &&
    !(sameSubject && application?.identity_number_last4)
      ? ['居民身份证号']
      : []),
    ...(subject === 'enterprise' &&
    !creditCode.trim() &&
    !(sameSubject && application?.unified_social_credit_code_last4)
      ? ['统一社会信用代码']
      : []),
    ...(subject === 'enterprise' && legalRepresentativeName.trim().length < 2
      ? ['法定代表人姓名']
      : []),
    ...(subject === 'enterprise' &&
    !legalRepresentativeID.trim() &&
    !(sameSubject && application?.legal_representative_id_last4)
      ? ['法定代表人居民身份证号']
      : []),
    ...requiredDocuments.filter((kind) => !byKind[kind]).map((kind) => documentLabel[kind]),
  ];
  useUnsavedChanges({
    dirty: editable && (dirty || busy),
    busy,
    save: saveDraft,
    discard: () => {
      if (verification.data) applyView(verification.data);
    },
  });
  async function run(name: string, action: () => Promise<void>): Promise<boolean> {
    if (pending.current || !editable) return false;
    pending.current = true;
    const scope = orgID();
    setOperation(name);
    setError(null);
    try {
      await action();
      return true;
    } catch (reason) {
      if (alive.current && scope === orgID()) setError(reason);
      return false;
    } finally {
      pending.current = false;
      if (alive.current && scope === orgID()) setOperation('');
    }
  }
  async function persistDraft() {
    const scope = orgID();
    const view = await post<OrganizationVerificationView>('/organization-verification', {
      subject_type: subject,
      expected_updated_at: version,
      legal_name: legalName.trim(),
      identity_number: identityNumber.trim(),
      unified_social_credit_code: creditCode.trim(),
      legal_representative_name: legalRepresentativeName.trim(),
      legal_representative_id_number: legalRepresentativeID.trim(),
      reference: reference.trim(),
    });
    if (alive.current && scope === orgID()) {
      applyView(view);
      qc.setQueryData(['/organization-verification', scope], view);
    }
  }
  async function saveDraft(): Promise<boolean> {
    if (!formRef.current?.reportValidity()) return false;
    return run('save', persistDraft);
  }
  async function upload(kind: string, file?: File) {
    if (!file || pending.current) return;
    if (file.size <= 0 || file.size > 10 * 1024 * 1024 || !/\.(jpe?g|png|pdf)$/i.test(file.name)) {
      setError(new Error('请选择 JPEG、PNG 或 PDF 文件，单个文件不超过 10 MB。'));
      return;
    }
    if (!formRef.current?.reportValidity()) return;
    await run(`upload:${kind}`, async () => {
      await persistDraft();
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      await api<OrganizationVerificationDocument>('/organization-verification/documents', {
        method: 'POST',
        body: form,
      });
      await qc.invalidateQueries({ queryKey: ['/organization-verification'] });
    });
  }
  async function deleteDocument(document: OrganizationVerificationDocument) {
    await run(`delete:${document.id}`, async () => {
      await remove(`/organization-verification/documents/${document.id}`);
      await qc.invalidateQueries({ queryKey: ['/organization-verification'] });
    });
  }
  async function submitForReview() {
    if (missing.length || !formRef.current?.reportValidity()) return;
    await run('submit', async () => {
      await persistDraft();
      const view = await post<OrganizationVerificationView>('/organization-verification/submit');
      if (alive.current) {
        applyView(view);
        qc.setQueryData(['/organization-verification', orgID()], view);
      }
      await qc.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).endsWith('/auth/session'),
      });
    });
  }

  return (
    <>
      <PageTitle
        title="主体认证"
        description="提交身份证或企业资质材料，由影爸爸运营人工审核。认证材料仅用于身份与资质审核，其他用户无法查看。"
      />
      <QueryState query={verification}>
        {(organization?.verification_materials_required ||
          organization?.verification_status === 'verified') &&
          verification.data?.status === 'unverified' && (
            <Notice>
              当前组织的认证资料需要补充。请填写主体信息、上传证件并提交审核，通过后即可申请数字资源供稿。
            </Notice>
          )}
        <div className="grid-2" style={{ marginTop: 20, alignItems: 'start' }}>
          <section className="panel stack">
            <div className="row spread">
              <h3 className="row">
                <ShieldCheck size={20} />
                当前认证状态
              </h3>
              <Status
                value={application?.status === 'draft' ? 'draft' : status}
                label={
                  application?.status === 'draft'
                    ? '资料草稿'
                    : {
                        unverified: '未认证',
                        pending_review: '审核中',
                        verified: '已认证',
                        rejected: '需重新提交',
                        revoked: '认证已撤销',
                      }[status]
                }
              />
            </div>
            <div className="stack small">
              <p>当前组织：{organization?.name || '—'}</p>
              <p>
                认证主体：
                {subjectLabel[application?.subject_type || verification.data?.subject_type || ''] ||
                  '尚未选择'}
              </p>
              {verification.data?.verified_at && (
                <p>认证时间：{datetime(verification.data.verified_at)}</p>
              )}
              {application?.legal_name && <p>认证名称：{application.legal_name}</p>}
              {application?.identity_number_last4 && (
                <p>身份证号：**************{application.identity_number_last4}</p>
              )}
              {application?.unified_social_credit_code_last4 && (
                <p>
                  统一社会信用代码：**************{application.unified_social_credit_code_last4}
                </p>
              )}
              {application?.legal_representative_id_last4 && (
                <p>法定代表人身份证号：**************{application.legal_representative_id_last4}</p>
              )}
              {application?.review_reason && <Notice>审核意见：{application.review_reason}</Notice>}
            </div>
            {status === 'verified' ? (
              <Notice>主体认证已经完成。如法定主体或实名身份发生变化，请联系平台运营处理。</Notice>
            ) : status === 'pending_review' ? (
              <Notice>
                材料正在人工审核。审核期间不可修改认证资料，你仍可浏览市场和管理已有项目。
              </Notice>
            ) : (
              <Notice>
                请准备清晰、完整且在有效期内的证件。敏感号码保存后仅显示尾号，材料仅供授权人员审核。
              </Notice>
            )}
          </section>

          <details
            className="panel verification-scope"
            open={scopeOpen}
            onToggle={(event) => setScopeOpen(event.currentTarget.open)}
          >
            <summary>认证与功能范围</summary>
            <ul className="prose small">
              <li>未认证：可浏览、建组织、维护资料、保存需求/服务草稿、发起沟通。</li>
              <li>已认证：可提交公开需求及团队、资源审核，参与合作报名和数字资源供稿。</li>
              <li>主体认证与团队、资源和数字作品审核分别进行。</li>
              <li>数字资源供稿与提现还需满足专项规则，主体认证不代表已开通支付或自动出款。</li>
            </ul>
          </details>
        </div>

        {editable && (
          <form
            ref={formRef}
            className="panel stack verification-editor"
            style={{ marginTop: 20 }}
            onSubmit={(event) => {
              event.preventDefault();
              void saveDraft();
            }}
          >
            <div className="row spread">
              <div>
                <h3>
                  {['rejected', 'revoked'].includes(status) ? '重新提交认证材料' : '填写认证材料'}
                </h3>
                <p className="muted small">
                  先保存基础资料，再上传证件；全部必需材料齐全后才能提交运营审核。
                </p>
              </div>
              {application?.status === 'draft' && <Status value="draft" label="草稿未提交" />}
            </div>
            {error != null && (
              <div ref={errorRef} tabIndex={-1} className="verification-error">
                <ErrorNotice error={error} />
              </div>
            )}
            {error instanceof ApiError && error.code === 'VERIFICATION_DRAFT_CHANGED' && (
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  leave(() => {
                    void verification.refetch().then((result) => {
                      if (result.data) {
                        applyView(result.data);
                        setError(null);
                      }
                    });
                  })
                }
              >
                重新载入最新草稿
              </Button>
            )}
            <fieldset disabled={busy} className="grid-2 verification-fields">
              <Field
                label="主体类型"
                htmlFor="verification-subject"
                hint={
                  documents.length > 0
                    ? '已有证件材料，删除全部材料后才能切换主体类型。'
                    : undefined
                }
              >
                <Select
                  id="verification-subject"
                  value={subject}
                  disabled={busy || documents.length > 0}
                  onChange={(event) => setSubject(event)}
                >
                  <option value="individual">个人</option>
                  <option value="enterprise">企业 / 公司</option>
                </Select>
              </Field>
              <Field
                label={subject === 'enterprise' ? '企业法定名称' : '真实姓名'}
                htmlFor="verification-legal-name"
              >
                <input
                  id="verification-legal-name"
                  className="input"
                  value={legalName}
                  minLength={2}
                  required
                  maxLength={120}
                  autoComplete={subject === 'individual' ? 'name' : 'organization'}
                  placeholder={
                    subject === 'enterprise'
                      ? '请输入营业执照上的企业法定名称'
                      : '请输入身份证上的真实姓名'
                  }
                  onChange={(e) => setLegalName(e.target.value)}
                />
              </Field>
              {subject === 'individual' ? (
                <Field
                  label="居民身份证号"
                  htmlFor="verification-identity-number"
                  hint="证件号码保存后只显示尾号，完整号码不会回传浏览器。"
                >
                  <input
                    id="verification-identity-number"
                    required={!(sameSubject && application?.identity_number_last4)}
                    className="input"
                    value={identityNumber}
                    maxLength={18}
                    inputMode="text"
                    autoComplete="off"
                    placeholder={
                      application?.identity_number_last4
                        ? `已保存，尾号 ${application.identity_number_last4}；留空不修改`
                        : '请输入 18 位居民身份证号码'
                    }
                    onChange={(e) => setIdentityNumber(e.target.value)}
                  />
                </Field>
              ) : (
                <>
                  <Field
                    label="统一社会信用代码"
                    htmlFor="verification-credit-code"
                    hint="请输入营业执照上的 18 位统一社会信用代码。"
                  >
                    <input
                      id="verification-credit-code"
                      required={!(sameSubject && application?.unified_social_credit_code_last4)}
                      className="input"
                      value={creditCode}
                      maxLength={18}
                      autoCapitalize="characters"
                      autoComplete="off"
                      placeholder={
                        application?.unified_social_credit_code_last4
                          ? `已保存，尾号 ${application.unified_social_credit_code_last4}；留空不修改`
                          : '请输入 18 位统一社会信用代码'
                      }
                      onChange={(e) => setCreditCode(e.target.value.toUpperCase())}
                    />
                  </Field>
                  <Field label="法定代表人姓名" htmlFor="verification-representative-name">
                    <input
                      id="verification-representative-name"
                      className="input"
                      value={legalRepresentativeName}
                      minLength={2}
                      required
                      maxLength={120}
                      autoComplete="name"
                      placeholder="请输入营业执照上的法定代表人姓名"
                      onChange={(e) => setLegalRepresentativeName(e.target.value)}
                    />
                  </Field>
                  <Field
                    label="法定代表人居民身份证号"
                    htmlFor="verification-representative-id"
                    hint="证件号码保存后只显示尾号，完整号码不会回传浏览器。"
                  >
                    <input
                      id="verification-representative-id"
                      required={!(sameSubject && application?.legal_representative_id_last4)}
                      className="input"
                      value={legalRepresentativeID}
                      maxLength={18}
                      autoComplete="off"
                      placeholder={
                        application?.legal_representative_id_last4
                          ? `已保存，尾号 ${application.legal_representative_id_last4}；留空不修改`
                          : '请输入 18 位居民身份证号码'
                      }
                      onChange={(e) => setLegalRepresentativeID(e.target.value)}
                    />
                  </Field>
                </>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <Field
                  label="补充说明（可选）"
                  htmlFor="verification-reference"
                  hint="如企业字号、证件信息近期发生变更，可在这里说明。"
                >
                  <textarea
                    id="verification-reference"
                    value={reference}
                    maxLength={1000}
                    placeholder="可填写证件变更、字号差异等需要运营关注的说明"
                    onChange={(e) => setReference(e.target.value)}
                  />
                </Field>
              </div>
            </fieldset>
            <div className="row spread wrap" style={{ paddingTop: 4 }}>
              <span className="muted small">可先保存草稿，补齐证件材料后再提交运营审核。</span>
              <Button
                type="submit"
                variant="secondary"
                loading={operation === 'save'}
                disabled={busy}
              >
                保存认证草稿
              </Button>
            </div>

            <div className="verification-progress" role="status">
              <strong>
                证件材料 {requiredDocuments.filter((kind) => !!byKind[kind]).length} /{' '}
                {requiredDocuments.length}
              </strong>
              <p>
                {missing.length
                  ? `待补充：${missing.join('、')}`
                  : '资料已齐全，请核对后提交审核。'}
              </p>
            </div>

            <div className="stack">
              <div>
                <h3>证件材料</h3>
                <p className="muted small">仅支持 JPEG、PNG、PDF，单个文件最大 10 MB。</p>
              </div>
              <div className="verification-documents">
                {requiredDocuments.map((kind) => {
                  const document = byKind[kind] as OrganizationVerificationDocument | undefined;
                  return document ? (
                    <div key={kind} className="stack" style={{ gap: 10 }}>
                      <PrivateDocumentPreview
                        path={`/organization-verification/documents/${document.id}/download`}
                        fileName={document.file_name}
                        mimeType={document.mime}
                        size={document.size}
                        label={documentLabel[kind]}
                      />
                      <div className="row spread wrap">
                        <span className="muted small">
                          可点击图片查看大图，PDF 可直接在线翻阅。
                        </span>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={busy}
                          loading={operation === `delete:${document.id}`}
                          onClick={() => void deleteDocument(document)}
                        >
                          <Trash2 size={15} /> 删除并重新上传
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div key={kind} className="verification-document-card">
                      <FileText size={24} className="muted" />
                      <div>
                        <strong>{documentLabel[kind]}</strong>
                        <p className="muted small">尚未上传 · JPEG、PNG 或 PDF</p>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() => documentInputs.current[kind]?.click()}
                        aria-label={`上传${documentLabel[kind]}`}
                      >
                        <Upload size={15} /> {uploadingKind === kind ? '上传中…' : '上传'}
                      </Button>
                      <input
                        ref={(element) => {
                          documentInputs.current[kind] = element;
                        }}
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        hidden
                        disabled={busy}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.currentTarget.value = '';
                          void upload(kind, file);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="form-actions wrap">
              <span className="muted small" style={{ marginRight: 'auto' }}>
                提交后材料将锁定，运营退回后才能重新编辑。
              </span>
              <Button
                type="button"
                onClick={() => void submitForReview()}
                loading={operation === 'submit'}
                disabled={busy || missing.length > 0}
              >
                提交运营审核
              </Button>
            </div>
          </form>
        )}

        {!canVerify && status !== 'verified' && (
          <section className="panel" style={{ marginTop: 20 }}>
            <Notice>
              {!active
                ? '当前工作空间只读，暂不能修改认证材料。'
                : '只有组织所有者或管理员可以填写和提交实名/企业认证材料。'}
            </Notice>
          </section>
        )}
      </QueryState>
    </>
  );
}
